import { IBlowDef, Segment } from './types';
import { seg } from './segments';
import { ParamReader } from './ParamReader';
import { sharedSpanListItems } from './spans';

/** blow 171's SkillType enum — distinct from the engine-wide SkillEnums.SkillType (different arity/labels). */
const SKILL_TYPES_171 = ['active', 'passive', 'autopassive', 'enchantpassive', 'autoactive', 'chainactive'];

/** Cooldown / MP-cost / skill-enhance mechanics. */
export const cooldowns: Record<number, IBlowDef> = {
    68: {
        name: 'Mana Refund',
        wikiDesc: 'Grants a chance of getting back the MP cost of a skill.',
        describe({ params }) {
            const f = params.numbers();
            const chance = f[0];
            if (isNaN(chance)) {
                return null;
            }
            const out: Segment[] = ['Grants a ', seg.pct(chance), ' chance to refund '];
            const mpCostPct = f[1];
            if (!isNaN(mpCostPct)) {
                out.push(seg.pct(mpCostPct), " of the skill's MP cost");
            } else {
                out.push("the skill's MP cost");
            }
            return out;
        },
    },
    96: {
        name: 'Skill Cooldown',
        wikiDesc: 'Skill cooldowns are reduced (-) or increased (+) by CD%. A ;1 after the CD removes the ' +
            'reduction for the casting skill.',
        describe({ params }) {
            const f = params.numbers();
            const ratio = f[0];
            if (isNaN(ratio)) {
                return null;
            }
            const dir = ratio < 0 ? 'reduced' : 'increased';
            const out: Segment[] = [`Skill cooldowns ${dir} by `, seg.pct(Math.abs(ratio))];
            if (f[1] === 1) {
                out.push(', excluding the casting skill');
            }
            return out;
        },
    },
    139: {
        name: 'MP usage',
        wikiDesc: 'MP usage ratio change: becomes 1+Mod%.',
        describe({ params }) {
            const mod = params.nextNum();
            if (isNaN(mod)) {
                return null;
            }
            return ['MP usage multiplier becomes ', seg.pct(1 + mod)];
        },
    },
    171: {
        name: 'Adjust skill cooldown',
        wikiDesc: "Multiplies the cooldown of the holder's skills of SkillType by Rate (0.8 = 20% " +
            'faster, 1.2 = slower), optionally restricted to the listed skill IDs. SkillType -1 = only ' +
            'the listed IDs. Never use Rate 0.',
        describe({ raw }) {
            const us = raw.split('_');
            const headReader = new ParamReader(us[0]);
            const head = headReader.fields();
            const type = ParamReader.num(head[0]);
            const rate = ParamReader.num(head[1]);
            if (isNaN(type) || isNaN(rate)) {
                return null;
            }
            const tailStr = us.length > 1 ? us.slice(1).join('_') : '';
            const tailReader = tailStr !== '' ? new ParamReader(tailStr, us[0].length + 1) : null;
            const skillTokens = tailReader ? tailReader.fields(';') : [];
            const items = sharedSpanListItems(skillTokens, tailReader, ';', seg.skill);
            const delta = 1 - rate;
            const dir = delta > 0 ? 'reduced' : 'increased';
            const out: Segment[] = [];
            if (type === -1) {
                if (!items.length) {
                    return null;
                }
                out.push('Cooldowns for ', seg.list(items));
            } else {
                out.push(
                    seg.en(type, SKILL_TYPES_171, 'unknown', headReader.span(0) || undefined),
                    ' skill cooldowns');
                if (items.length) {
                    out.push(' (', seg.list(items), ')');
                }
            }
            out.push(` ${dir} by `, seg.pct(Math.abs(delta), 2, headReader.span(1) || undefined));
            return out;
        },
    },
    269: {
        name: 'Ultimate Skill Cooldown Boost',
        wikiDesc: 'Cooldown of skills with a SkillGroupID > 0 (ultimates) is reduced by Rate; ' +
            'instances sum. The reduction is given back to running cooldowns when this effect ends.',
        describe({ params }) {
            const rate = params.nextNum();
            if (isNaN(rate)) {
                return null;
            }
            return [
                'Reduces the cooldown of ultimate skills (SkillGroupID > 0) by ', seg.pct(rate),
                ' (instances sum; refunded to running cooldowns when this ends)',
            ];
        },
    },
    291: {
        name: 'All active skill cooldown refund',
        wikiDesc: 'Refund RefundSeconds seconds of cooldown to all active skills.',
        describe({ params }) {
            const secs = params.nextNum();
            if (isNaN(secs)) {
                return null;
            }
            return ['Refunds ', seg.secs(secs * 1000), ' of cooldown to all active skills'];
        },
    },
    304: {
        name: 'Increase Cooltime Charging',
        wikiDesc: 'Sets the CTC (cooldown-time-charging) count of the given skills.',
        describe({ params }) {
            const f = params.fields();
            if (f.length < 2) {
                return null;
            }
            const skillIds = ParamReader.numList(f[0], ',');
            const ctcCount = ParamReader.num(f[1]);
            if (!skillIds.length || isNaN(ctcCount)) {
                return null;
            }
            return [
                'Sets the CTC count of ', seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                ' to ', seg.num(ctcCount),
            ];
        },
    },
    328: {
        name: 'Dragon Gem - Decrease Cooltime1',
        wikiDesc: "Normal (non-skill) attack hits have Percent% chance to apply SubSkillID's effects " +
            '(put a 333 in the sub skill for the cooldown refund).',
        note: 'Cooldown is given in SECONDS here, unlike 329/330 which use ms.',
        describe({ params }) {
            const f = params.fields();
            const subSkillId = ParamReader.num(f[0]);
            const cooldownSec = ParamReader.num(f[1]);
            const percent = ParamReader.num(f[2]);
            if (isNaN(subSkillId) || isNaN(cooldownSec) || isNaN(percent)) {
                return null;
            }
            const out: Segment[] = [
                'Normal-attack hits have ', seg.pct(percent / 100), ' chance to apply ',
                seg.skill(subSkillId), "'s effects (put a ", seg.blow(333),
                ' inside for the cooldown refund)',
            ];
            if (cooldownSec > 0) {
                out.push(' (', seg.secs(cooldownSec * 1000), ' cooldown)');
            }
            return out;
        },
    },
    329: {
        name: 'Dragon Gem - Decrease Cooltime2',
        wikiDesc: 'Hitting with a down-attack skill gives Percent% chance to refund the remaining ' +
            'cooldown of your ultimates (SkillGroupID > 0), via a 333 inside SubSkillID. Skills flagged ' +
            'IsIgnoreCoolTimeReduction are skipped.',
        describe({ params }) {
            const f = params.fields();
            const subSkillId = ParamReader.num(f[0]);
            const cooldownMs = ParamReader.num(f[1]);
            const percent = ParamReader.num(f[2]);
            if (isNaN(subSkillId) || isNaN(cooldownMs) || isNaN(percent)) {
                return null;
            }
            const out: Segment[] = [
                'Hitting with a down-attack skill has ', seg.pct(percent / 100),
                ' chance to refund the cooldown of your ultimates, via ', seg.skill(subSkillId),
                ' (skills flagged to ignore cooldown reduction are skipped)',
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    330: {
        name: 'Dragon Gem - Decrease Cooltime3',
        wikiDesc: 'Hitting an enemy within Distance (edge to edge) has Percent% chance to apply ' +
            "SubSkillID's effects to self.",
        describe({ params }) {
            const f = params.fields();
            const subSkillId = ParamReader.num(f[0]);
            const cooldownMs = ParamReader.num(f[1]);
            const percent = ParamReader.num(f[2]);
            const distance = ParamReader.num(f[3]);
            if (isNaN(subSkillId) || isNaN(cooldownMs) || isNaN(percent) || isNaN(distance)) {
                return null;
            }
            const out: Segment[] = [
                'Hitting an enemy within ', seg.num(distance), ' range has ', seg.pct(percent / 100),
                ' chance to apply ', seg.skill(subSkillId), "'s effects to self",
            ];
            if (cooldownMs > 0) {
                out.push(' (', seg.secs(cooldownMs), ' cooldown)');
            }
            return out;
        },
    },
    333: {
        name: 'Skill Reduce CD',
        wikiDesc: 'Reduces the cooldown of the listed skills by a percent and a flat ms amount.',
        note: 'Avoid stacking multiple of these on the same applying skill.',
        describe({ params }) {
            const f = params.fields();
            const pct = ParamReader.num(f[0]);
            const flatMs = ParamReader.num(f[1]);
            const skillIdsFieldSpan = params.span(2);
            const skillIdsReader = skillIdsFieldSpan ? new ParamReader(f[2], skillIdsFieldSpan[0]) : null;
            const skillIdTokens = f.length > 2 ? ParamReader.list(f[2], ',') : [];
            const items = sharedSpanListItems(skillIdTokens, skillIdsReader, ',', seg.skill);
            if (isNaN(pct) || isNaN(flatMs) || !items.length) {
                return null;
            }
            const out: Segment[] = [
                'Reduces the cooldown of ', seg.list(items),
                ' by ', seg.pct(pct / 100, 2, params.span(0) || undefined), ' and ',
                seg.secs(flatMs, params.span(1) || undefined),
            ];
            if (f[3] === '1') {
                out.push(" (overrides the skill's ignore-cooldown-reduction flag, if set)");
            }
            return out;
        },
    },
    338: {
        name: 'Maximum Skill Cooltime Reduce',
        wikiDesc: 'Reduces skill cooldown by a percent and a flat ms amount, optionally capped by a ' +
            'maximum total reduction and scoped to a single skill.',
        describe({ params }) {
            const f = params.fields();
            const pct = ParamReader.num(f[0]);
            const flatMs = ParamReader.num(f[1]);
            if (isNaN(pct) || isNaN(flatMs)) {
                return null;
            }
            const out: Segment[] = ['Reduces skill cooldown'];
            const parts: Segment[][] = [];
            if (!isNaN(pct) && pct) {
                parts.push([seg.pct(pct / 100)]);
            }
            if (!isNaN(flatMs) && flatMs) {
                parts.push([seg.secs(flatMs)]);
            }
            if (parts.length) {
                out.push(' by ', seg.list(parts));
            }
            const maxReductionMs = f.length > 2 ? ParamReader.num(f[2]) : NaN;
            if (!isNaN(maxReductionMs)) {
                out.push(', capped at ', seg.secs(maxReductionMs), ' total reduction');
            }
            const skillId = f.length > 3 ? ParamReader.num(f[3]) : NaN;
            if (!isNaN(skillId)) {
                out.push(' for ', seg.skill(skillId));
            }
            return out;
        },
    },
    339: {
        name: 'Use Skill MP Ratio',
        wikiDesc: 'MP cost of the listed skills becomes cost * (1 - Ratio); also applies to their ' +
            'continuous MP-drain buff ticks.',
        describe({ params }) {
            const f = params.fields();
            const ratio = ParamReader.num(f[0]);
            const skillIds = f.length > 1 ? ParamReader.numList(f[1], ',') : [];
            if (isNaN(ratio) || !skillIds.length) {
                return null;
            }
            return [
                'MP cost of ', seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])),
                ' reduced by ', seg.pct(ratio), ' (also applies to their continuous MP-drain buff ticks)',
            ];
        },
    },
    351: {
        name: 'Disable use of skill',
        wikiDesc: 'Disables the listed skill IDs for the duration.',
        describe({ raw }) {
            const ids = raw.split('/').map((s) => ParamReader.num(s.trim())).filter((v) => !isNaN(v));
            if (!ids.length) {
                return null;
            }
            return ['Disables ', seg.list(ids.map((id) => [seg.skill(id)] as Segment[]))];
        },
    },
    356: {
        name: 'Skill CDR on skill hit',
        wikiDesc: 'Reduces the cooldown of the recovered skills by RecoveryAmount ms when any of the ' +
            'recovery skills hit.',
        describe({ params }) {
            const f = params.fields();
            const recoveredIds = ParamReader.numList(f[0], ',');
            const recoveryIds = f.length > 1 ? ParamReader.numList(f[1], ',') : [];
            const amount = ParamReader.num(f[2]);
            if (!recoveredIds.length || !recoveryIds.length || isNaN(amount)) {
                return null;
            }
            const idListItems = (fieldIndex: number): Segment[][] => {
                const fieldSpan = params.span(fieldIndex);
                const fieldReader = fieldSpan ? new ParamReader(f[fieldIndex], fieldSpan[0]) : null;
                const tokens = ParamReader.list(f[fieldIndex], ',');
                return sharedSpanListItems(tokens, fieldReader, ',', seg.skill);
            };
            return [
                'Reduces the cooldown of ', seg.list(idListItems(0)), ' by ',
                seg.secs(amount, params.span(2) || undefined),
                ' when ', seg.list(idListItems(1)), ' hits',
            ];
        },
    },
    357: {
        name: 'Decrease cooltime of skill for being airborne',
        wikiDesc: 'After being airborne for minAirTimeMs, resets the CD of the listed skills.',
        describe({ params }) {
            const f = params.fields();
            const minAirTimeMs = ParamReader.num(f[0]);
            const skillIds = f.length > 1 ? ParamReader.numList(f[1], ',') : [];
            if (isNaN(minAirTimeMs) || !skillIds.length) {
                return null;
            }
            return [
                'After being airborne for ', seg.secs(minAirTimeMs), ', resets the cooldown of ',
                seg.list(skillIds.map((id) => [seg.skill(id)] as Segment[])), ' (archers only)',
            ];
        },
    },
    358: {
        name: 'Decrease cooldown of skill',
        wikiDesc: 'For each skillID;decreaseCooldownTimeMs pair: reduces that cooldown by the given ms, ' +
            'or resets it fully if -1. A comma-appended value after -1 refunds that many additional CTC ' +
            'stacks (`1234;-1,0` refunds 1 stack, `1234;-1,1` refunds 2, etc).',
        describe({ raw }) {
            const f = raw.split(';').map((s) => s.trim());
            const items: Segment[][] = [];
            for (let i = 0; i + 1 < f.length; i += 2) {
                const skillId = ParamReader.num(f[i]);
                if (isNaN(skillId)) {
                    continue;
                }
                const spec = f[i + 1];
                const comma = spec.indexOf(',');
                const msStr = comma >= 0 ? spec.slice(0, comma) : spec;
                const ms = ParamReader.num(msStr);
                if (isNaN(ms)) {
                    continue;
                }
                const item: Segment[] = [seg.skill(skillId), ': '];
                if (ms === -1) {
                    const extraCtc = comma >= 0 ? ParamReader.num(spec.slice(comma + 1)) : NaN;
                    if (!isNaN(extraCtc) && extraCtc > 0) {
                        item.push('resets CD fully, refunding ', seg.num(extraCtc + 1), ' CTC stacks');
                    } else if (comma >= 0) {
                        item.push('resets CD fully, refunding 1 CTC stack');
                    } else {
                        item.push('resets CD and all CTC stacks');
                    }
                } else {
                    item.push('reduces CD by ', seg.secs(ms));
                }
                items.push(item);
            }
            if (!items.length) {
                return null;
            }
            return [seg.list(items)];
        },
    },
    375: {
        name: 'Increase Duration',
        wikiDesc: 'Increases the duration of the state effects applied by SkillID by AddTimeMs, up ' +
            "to a maximum of MaxTimeMs, optionally applying SkillID's buff first if the holder " +
            "doesn't have it.",
        describe({ params }) {
            const f = params.fields();
            const skillId = ParamReader.num(f[0]);
            const addTimeMs = ParamReader.num(f[1]);
            const maxTimeMs = ParamReader.num(f[2]);
            if (isNaN(skillId) || isNaN(addTimeMs) || isNaN(maxTimeMs)) {
                return null;
            }
            const out: Segment[] = [
                'Increases the duration of the state effects applied by ', seg.skill(skillId), ' by ',
                seg.secs(addTimeMs), ', up to a maximum of ', seg.secs(maxTimeMs),
            ];
            if (f[3] === '1') {
                out.push(", applying the skill's buff first if the holder doesn't have it");
            }
            return out;
        },
    },
    377: {
        name: 'Set Global Cooldown',
        wikiDesc: 'Sets all skills with the given skillGroupID onto the given cooldown. If global is ' +
            'set, all actors on the same team as the applied actor are affected (rather than only ' +
            'actors that receive this state effect).',
        describe({ params }) {
            const f = params.fields();
            const groupId = ParamReader.num(f[0]);
            const cooldownMs = ParamReader.num(f[1]);
            if (isNaN(groupId) || isNaN(cooldownMs)) {
                return null;
            }
            const out: Segment[] = [
                'Sets skills in skill group ', seg.id(groupId, params.span(0) || undefined), ' onto a ',
                seg.secs(cooldownMs, params.span(1) || undefined), ' cooldown',
            ];
            const GLOBAL_SCOPES = ['affecting only this actor', 'affecting the whole team'];
            const global = f.length > 2 && f[2] !== '' ? ParamReader.num(f[2]) : NaN;
            if (!isNaN(global)) {
                out.push(', ', seg.en(global, GLOBAL_SCOPES, 'unknown scope', params.span(2) || undefined));
            }
            return out;
        },
    },
};
